import { LocalCanvasHost } from "@/components/canvas/LocalCanvasHost.tsx";
import { RightSidebarHeader } from "@/components/right-sidebar/elements.tsx";
import {
  useRightSidebar,
  useRightSidebarQueryParamState,
} from "@/components/right-sidebar/hooks.tsx";
import { SidebarContent } from "@/components/ui/sidebar.tsx";
import { ContactDetailSidebar } from "./ContactDetailSidebar.tsx";
import { ContactList } from "./ContactList.tsx";

/**
 * The sidebar's own frame, and the canvas the panel presents into: an event or
 * a routine opened from a contact's activity is a card over this panel. Without
 * one the row falls outward — onto the screen's canvas, which is a different
 * column beside this one, or with no canvas at all to a real navigation, which
 * takes the sidebar off screen with the page it was opened over.
 *
 * The host sits here rather than on `ContactDetailSidebar` because the canvas
 * stack renders that file's `ContactDetailBody`; importing a host there would
 * close an import cycle back through `CanvasContactModal`. Here is also simply
 * where the frame is, which is the box a card should cover.
 */
export const ContactListSidebar = ({
  contactId,
  defaultTab,
}: {
  contactId?: string;
  defaultTab?: "communications" | "events" | "bio";
}) => {
  const { closeSidebar } = useRightSidebar();
  // Desktop keeps this content mounted through the close animation, so being
  // rendered is not being on screen. The header stops offering its slot the
  // moment the sidebar closes; a card over it has to do the same, or the
  // controls ride it off screen instead of returning to the page header.
  const isOpen = !!useRightSidebarQueryParamState();

  // Every producer of this sidebar names a contact — the boundary from a
  // `/contacts/:id` link, and the all-contacts list from a row click. Show the
  // richer detail view (avatar, handles with icons, badges) rather than a
  // one-item ContactList row.
  if (contactId) {
    return (
      // Keyed, so the canvas belongs to the contact it was opened over. The
      // sidebar is swapped in place — a row in the list beside it re-opens this
      // same component on someone else — and an unkeyed host would keep its
      // stack across that, leaving one person's event card over another's
      // details. The same reason the activity tabs key on the contact.
      <LocalCanvasHost key={contactId} headerUtilityLayer={isOpen ? "sidebar" : null}>
        <ContactDetailSidebar contactId={contactId} defaultTab={defaultTab} />
      </LocalCanvasHost>
    );
  }

  // Reached only by a hand-written or stale `sb_type=contactList` URL that names
  // nobody. Falling back to the browsable list beats rendering an empty panel.
  return (
    <>
      <RightSidebarHeader title="People" handleClose={closeSidebar} />
      <SidebarContent className="px-4 py-4">
        <ContactList />
      </SidebarContent>
    </>
  );
};
